import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import {
  ProductBreadcrumbs,
  ProductDetailsTabs,
  ProductImageGallery,
  ProductImportantInformation,
  ProductInfo,
  ProductPurchaseActions,
  ProductPurchaseBox,
  ProductReviews,
} from "../../components/frontend/products";
import ProductRefreshStatus from "../../components/frontend/products/ProductRefreshStatus";
import ShippingInfoCard from "../../components/common/ShippingInfoCard";
import { useProductDetails } from "../../hooks/useProductDetails";
import { useProductOffers } from "../../hooks/useProductOffers";
import { useShippingCalculation } from "../../hooks/useShippingCalculation";
import { Offer, ProductDetailsResponse } from "../../types/products";
import { formatApiError } from "../../utils/formatApiError";
// import { normalizePrice } from "../../utils/priceUtils";
import {
  normalizeBackendPrice,
  normalizeRegularPriceWithContext,
} from "../../utils/priceUtils";
import { getVariantSpecValue } from "../../utils/productVariantUtils";
import {
  extractCategoryFromProduct,
  extractPackageDimensionsFromProduct,
  hasValidPackageDimensions,
} from "../../utils/productUtils";
import {
  trackInitiateCheckout,
  trackViewContent,
} from "../../utils/metaPixel";
import { useTranslation } from "react-i18next";

const normalizeProductDetails = (
  details: string[]
): Array<{ name: string; value: string }> => {
  const converted = details
    .filter((detail) => !detail.toLowerCase().includes("customer reviews"))
    .map((detail) => {
      const parts = detail.split(": ");
      if (parts.length >= 2) {
        return { name: parts[0], value: parts.slice(1).join(": ") };
      }
      return { name: "Detail", value: detail };
    });

  const seen = new Set<string>();
  return converted.filter((item) => {
    const normalizedName = item.name
      .toLowerCase()
      .trim()
      .replace(/ies$/g, "y")
      .replace(/ves$/g, "f")
      .replace(/s$/, "")
      .replace(/technologys?$/g, "technology")
      .replace(/specifications?$/g, "specification")
      .replace(/dimensions?$/g, "dimension")
      .replace(/capabilities?$/g, "capability")
      .replace(/qualities?$/g, "quality")
      .replace(/capacities?$/g, "capacity")
      .replace(/quantities?$/g, "quantity")
      .replace(/accessories?$/g, "accessory")
      .replace(/categories?$/g, "category")
      .replace(/memories?$/g, "memory")
      .replace(/stories?$/g, "story")
      .replace(/companies?$/g, "company")
      .replace(/families?$/g, "family")
      .replace(/studies?$/g, "study")
      .replace(/batteries?$/g, "battery")
      .replace(/series?$/g, "series")
      .replace(/species?$/g, "species");

    if (seen.has(normalizedName)) {
      return false;
    }
    seen.add(normalizedName);
    return true;
  });
};

const parseReviewSummary = (details?: string[]) => {
  if (!details?.length) {
    return { stars: null, reviewCount: null };
  }

  const entry = details.find((detail) =>
    detail.toLowerCase().includes("customer reviews")
  );

  if (!entry) {
    return { stars: null, reviewCount: null };
  }

  const rawText = entry.split(":").slice(1).join(":").trim() || entry;
  const starsMatch = rawText.match(/([0-9]+(?:\.[0-9]+)?)\s*out of 5/i);
  const starsValue = starsMatch
    ? Number(starsMatch[1])
    : Number(rawText.match(/([0-9]+(?:\.[0-9]+)?)/)?.[1] ?? NaN);

  let reviewCountValue: number | null = null;
  const parenMatch = rawText.match(/\(([\d,]+)\s*(?:ratings?|reviews?)?\)/i);
  if (parenMatch?.[1]) {
    reviewCountValue = Number(parenMatch[1].replace(/,/g, ""));
  } else {
    const countMatch = rawText.match(/([\d,]+)\s*(?:ratings?|reviews?)\b/i);
    if (countMatch?.[1]) {
      reviewCountValue = Number(countMatch[1].replace(/,/g, ""));
    }
  }

  return {
    stars: Number.isFinite(starsValue) ? starsValue : null,
    reviewCount: Number.isFinite(reviewCountValue) ? reviewCountValue : null,
  };
};

const ProductDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { productId } = useParams<{ productId: string }>();
  const [searchParams] = useSearchParams();
  const retailer = searchParams.get("retailer") || "amazon";
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Capture origin search context if present in referrer state or URL
  const fromSearchQuery =
    (location.state as any)?.fromSearch?.query ||
    new URLSearchParams(location.search).get("q") ||
    new URLSearchParams(document.referrer.split("?")[1] || "").get("query");
  const fromSearchRetailer =
    (location.state as any)?.fromSearch?.retailer ||
    new URLSearchParams(location.search).get("retailer") ||
    "amazon";

  // Local UI state

  // State for variant selections
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedStyle, setSelectedStyle] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedProvider, setSelectedProvider] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedCapacity, setSelectedCapacity] = useState("");
  const [selectedSet, setSelectedSet] = useState("");
  const [selectedConfiguration, setSelectedConfiguration] = useState("");
  const [selectedFlavorName, setSelectedFlavorName] = useState("");
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState(0);
  const trackedViewContentRef = useRef<string>("");

  const { data, isLoading, error } = useProductDetails({
    productId: productId || "",
    retailer,
    enabled: Boolean(productId),
  });

  // Extract shipping calculation data from raw data
  const category = data ? extractCategoryFromProduct(data) : "Electronics";
  const packageDimensions = data
    ? extractPackageDimensionsFromProduct(data)
    : null;
  const hasValidDimensions = hasValidPackageDimensions(packageDimensions);

  // Shipping calculation hook
  // Enable calculation if we have productId OR productTitle OR valid dimensions
  // This allows calculation even when package dimensions are missing
  const {
    shippingData,
    isLoading: isShippingLoading,
    error: shippingError,
    refetch: refetchShipping,
    isFetched: isShippingFetched,
  } = useShippingCalculation({
    productId: productId || data?.product_id || data?.asin,
    retailer,
    category,
    packageDimensions: packageDimensions || undefined,
    productTitle: data?.title || "",
    enabled: !!data && (!!productId || !!data?.title || hasValidDimensions),
  });

  // Fetch offers (real pricing, delivery, seller info) after shipping loads
  const {
    buyBoxOffer,
    allOffers,
    isLoading: isOffersLoading,
  } = useProductOffers({
    productId: productId || "",
    retailer,
    enabled: Boolean(productId) && !!data && isShippingFetched,
  });

  // Seller selection state — null = use buy box winner (default best offer)
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  // Reset seller selection when navigating to a different product
  useEffect(() => setSelectedOffer(null), [productId]);
  const activeOffer = selectedOffer ?? buyBoxOffer;
  const offersPrice = activeOffer?.price != null ? activeOffer.price / 100 : null;

  // Check if phone is locked (Wireless Carrier doesn't contain "unlock")
  const isPhoneLocked = useMemo(() => {
    const details = data?.product_details || [];
    // Find Wireless Carrier entry in the string array format "Key: Value"
    const carrierEntry = details.find((detail: string) =>
      detail.toLowerCase().startsWith("wireless carrier:")
    );

    if (!carrierEntry) return false;

    // Extract the value part after the colon
    const carrierValue = carrierEntry
      .split(":")
      .slice(1)
      .join(":")
      .trim()
      .toLowerCase();

    // Check if it's unlocked - if it contains "unlock" it's unlocked, otherwise it's locked
    return carrierValue ? !carrierValue.includes("unlock") : false;
  }, [data?.product_details]);

  // Check if AppleCare+ is selected (Configuration or Set) - but NOT "Without AppleCare+"
  const isAppleCareSelected = useMemo(() => {
    const configAppleCare =
      selectedConfiguration?.toLowerCase().includes("applecare") &&
      !selectedConfiguration?.toLowerCase().includes("without applecare");
    const setAppleCare =
      selectedSet?.toLowerCase().includes("applecare") &&
      !selectedSet?.toLowerCase().includes("without applecare");
    return configAppleCare || setAppleCare;
  }, [selectedConfiguration, selectedSet]);

  // Check if product title contains "with AppleCare+" (but not "Without AppleCare+")
  const isAppleCareInTitle = useMemo(() => {
    if (!data?.title) return false;

    const title = data.title.toLowerCase();

    // Check for "with applecare" but exclude "without applecare"
    return (
      title.includes("with applecare") && !title.includes("without applecare")
    );
  }, [data?.title]);

  // Combined AppleCare+ detection (variant selection OR title)
  const isAppleCareRestricted = useMemo(() => {
    return isAppleCareSelected || isAppleCareInTitle;
  }, [isAppleCareSelected, isAppleCareInTitle]);

  const handleBackToSearch = useCallback(() => {
    navigate("/search");
  }, [navigate]);

  const handleVariantSelect = useCallback(
    (newProductId: string) => {
      navigate(`/product/${newProductId}?retailer=${retailer}`);
    },
    [navigate, retailer]
  );

  // Ensure we start at the top when navigating to this page
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    if (!data) return;
    const asin = (data.asin || data.product_id) as string;
    const variants = (data.all_variants || []) as any[];
    const current = variants.find(
      (v) => v.product_id === asin || v.asin === asin
    );
    if (!current) return;
    const get = (dimension: string) =>
      getVariantSpecValue(current, dimension) || "";
    const curSize = get("Size");
    const curStyle = get("Style");
    const curColor = get("Color");
    const curFlavorName = get("Flavor Name");
    const curProvider = get("Service Provider");
    const curGrade = get("Product Grade");
    const curCapacity = get("Capacity");
    const curSet = get("Set");
    const curConfiguration = get("Configuration");
    if (curSize) setSelectedSize(curSize);
    if (curStyle) setSelectedStyle(curStyle);
    if (curColor) setSelectedColor(curColor);
    if (curFlavorName) setSelectedFlavorName(curFlavorName);
    if (curProvider) setSelectedProvider(curProvider);
    if (curGrade) setSelectedGrade(curGrade);
    if (curCapacity) setSelectedCapacity(curCapacity);
    if (curSet) setSelectedSet(curSet);
    if (curConfiguration) setSelectedConfiguration(curConfiguration);
  }, [data]);
  // Update global breadcrumb label for product page to use last category (e.g., "Vacuums")
  useEffect(() => {
    const categories =
      (data as ProductDetailsResponse | undefined)?.categories || [];
    const lastCategory =
      categories.length > 0 ? categories[categories.length - 1] : "";
    const title = (data as ProductDetailsResponse | undefined)?.title;
    const asin = (data as ProductDetailsResponse | undefined)?.asin;
    const label = lastCategory || title || asin || "";
    if (label) {
      // Persist for non-product pages that render later
      try {
        sessionStorage.setItem("breadcrumb_last_label", label);
      } catch (_e) { }
      // Also update router state to trigger a re-render of PageLayout breadcrumbs
      const currentState = (location.state || {}) as Record<string, unknown>;
      const stateLabel = (currentState["breadcrumbLabel"] as string) || "";
      if (stateLabel !== label) {
        navigate(location.pathname + location.search, {
          replace: true,
          state: { ...currentState, breadcrumbLabel: label },
        });
      }
    }
  }, [data, location.pathname, location.search, location.state, navigate]);

  const productData = data as ProductDetailsResponse | undefined;

  // Normalize prices with context-aware adjustment for ambiguous 4-digit integers
  const normalizedListPrice = useMemo(
    () =>
      productData?.original_retail_price
        ? normalizeBackendPrice(productData.original_retail_price, "list")
        : null,
    [productData?.original_retail_price]
  );
  const normalizedRegularPrice = useMemo(
    () =>
      productData
        ? normalizeRegularPriceWithContext(productData.price, normalizedListPrice)
        : null,
    [productData, normalizedListPrice]
  );
  const trackedContentId =
    productData?.product_id || productData?.asin || productId || "";
  const trackedContentName = productData?.title || "";
  const trackedContentCategory = productData?.categories?.length
    ? productData.categories[productData.categories.length - 1]
    : undefined;
  const trackedContentCurrency = productData?.currency || "USD";

  useEffect(() => {
    if (!trackedContentId || !trackedContentName) return;

    const trackingKey = `${trackedContentId}:${normalizedRegularPrice ?? "na"}`;
    if (trackedViewContentRef.current === trackingKey) return;

    trackViewContent({
      contentId: trackedContentId,
      contentName: trackedContentName,
      contentCategory: trackedContentCategory,
      value: normalizedRegularPrice ?? undefined,
      currency: trackedContentCurrency,
    });

    trackedViewContentRef.current = trackingKey;
  }, [
    trackedContentCategory,
    trackedContentCurrency,
    trackedContentId,
    trackedContentName,
    normalizedRegularPrice,
  ]);
  const normalizedProductDetails = useMemo(
    () => normalizeProductDetails(productData?.product_details || []),
    [productData?.product_details]
  );
  const parsedReviewSummary = useMemo(
    () => parseReviewSummary(productData?.product_details),
    [productData?.product_details]
  );
  const resolvedStars =
    typeof productData?.stars === "number" && productData.stars > 0
      ? productData.stars
      : (parsedReviewSummary.stars ?? 0);
  const resolvedReviewCount =
    typeof productData?.review_count === "number" &&
      productData.review_count > 0
      ? productData.review_count
      : typeof productData?.num_reviews === "number" &&
        productData.num_reviews > 0
        ? productData.num_reviews
        : (parsedReviewSummary.reviewCount ?? 0);

  // Create product object from clean DTO data
  const product = useMemo(
    () => ({
      title: productData?.title || t("products.productTitleNotAvailable"),
      brand: productData?.brand || "Brand Not Available",
      // Use offers price (accurate) if available, fallback to details price
      price: offersPrice ?? normalizedRegularPrice ?? null,
      originalPrice: normalizedListPrice,
      stars: resolvedStars,
      reviewCount: resolvedReviewCount,
      numSales: productData?.num_sales || 0,
      description:
        productData?.product_description || productData?.description || "",
      features: productData?.feature_bullets || [],
      images: productData?.images || [],
      mainImage: productData?.main_image || "",
      productDetails: normalizedProductDetails,
      categories: productData?.categories || [],
      allVariants: productData?.all_variants || [],
      returnHint: productData?.return_hint_text || "",
      numOffers: productData?.num_offers || 0,
      asin: productData?.asin || productData?.product_id || "",
      packageDimensions: productData?.package_dimensions || null,
      condition: productData?.condition || "New",
      seller: productData?.seller || "Amazon",
      stockStatus: productData?.stock_status || "In Stock",
      deliveryInfo: productData?.delivery_info || "FREE delivery",
      availability: productData?.availability || "In Stock",
      shippingWeight: productData?.shipping_weight || "1.2 lbs",
      warranty: productData?.warranty || "1 Year Limited Warranty",
      modelNumber: productData?.model_number || "N/A",
      upc: productData?.upc || "N/A",
      epids: productData?.epids || [],
      epidsMap: productData?.epids_map || {},
      timestamp: productData?.timestamp || 0,
      authors: productData?.authors || [],
      itemLocation: productData?.item_location || "",
      itemNumber: productData?.item_number || "",
      // Product flags
      fresh: productData?.fresh || false,
      pantry: productData?.pantry || false,
      handmade: productData?.handmade || false,
      customizable: productData?.customizable || false,
      digital: productData?.digital || false,
      digitalSubscription: productData?.digital_subscription || false,
      addon: productData?.addon || false,
      prime: productData?.prime || false,
      freeShipping: productData?.free_shipping || false,
      fulfilledByAmazon: productData?.fulfilled_by_amazon || false,
      giftCard: productData?.gift_card || false,
      eliapo: productData?.eliapo || false,
      blankBox: productData?.blank_box || false,
      buyapiHint: productData?.buyapi_hint || false,
      // Stock information - mock based on stock status for now
      stockQuantity:
        productData?.stock_status === "In Stock"
          ? 15
          : productData?.stock_status === "Only a few left"
            ? 4
            : productData?.stock_status === "Low Stock"
              ? 2
              : 4,
    }),
    [
      productData,
      offersPrice,
      normalizedRegularPrice,
      normalizedListPrice,
      resolvedStars,
      resolvedReviewCount,
      normalizedProductDetails,
    ]
  );

  const handleBuyNow = useCallback(() => {
    if (!productData) return;
    const buyNowId =
      productData.product_id ||
      productData.asin ||
      product.asin ||
      productId ||
      "";
    const itemPrice = offersPrice ?? (Number(product.price) || Number(productData.price) || 0);
    // Offer's internal shipping fee (seller charges within US)
    const offerShipFee = activeOffer?.shipping_options?.[0]?.price
      ? activeOffer.shipping_options[0].price / 100
      : 0;
    const checkoutValue = (itemPrice * quantity) + offerShipFee;

    const shippingCost =
      shippingData?.shippingCost !== undefined
        ? shippingData.shippingCost
        : undefined;

    const buyNowPayload = {
      id: buyNowId,
      title: product.title,
      price: offersPrice ?? (Number(product.price) || Number(productData.price) || 0),
      offerShippingFee: offerShipFee || undefined,
      sellerName: activeOffer?.seller?.name,
      sellerId: activeOffer?.seller?.id,
      offerId: activeOffer?.offer_id,
      quantity,
      image:
        product.mainImage || product.images[0] || "/placeholder-product.png",
      shippingCost,
      retailer: productData.retailer || "Unknown",
      shippingCalculated: shippingCost !== undefined,
    };

    trackInitiateCheckout({
      value: checkoutValue,
      currency: productData.currency || "USD",
      numItems: quantity,
      contentIds: buyNowId ? [buyNowId] : undefined,
      contents: buyNowId
        ? [{ id: buyNowId, quantity, item_price: itemPrice }]
        : undefined,
    });

    navigate("/checkout", {
      state: buyNowId ? { buyNowItem: buyNowPayload } : undefined,
    });
  }, [
    activeOffer,
    navigate,
    offersPrice,
    product,
    productData,
    productId,
    quantity,
    shippingData?.shippingCost,
  ]);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
    },
    []
  );

  const hasDetailsTabs =
    product.productDetails.length > 0 || product.features.length > 0;

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Breadcrumbs skeleton */}
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="text" width={300} height={32} />
        </Box>

        {/* Main layout skeleton */}
        <Grid container spacing={4}>
          {/* Left Column - Image gallery skeleton */}
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={400} />
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Skeleton variant="rectangular" width={60} height={60} />
              <Skeleton variant="rectangular" width={60} height={60} />
              <Skeleton variant="rectangular" width={60} height={60} />
              <Skeleton variant="rectangular" width={60} height={60} />
            </Box>
          </Grid>

          {/* Middle Column - Product info skeleton */}
          <Grid item xs={12} md={5}>
            <Skeleton variant="text" width="80%" height={40} />
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
          </Grid>

          {/* Right Column - Purchase box and side widgets skeleton */}
          <Grid item xs={12} md={3}>
            <Skeleton variant="rectangular" height={300} />
            <Skeleton variant="rectangular" height={150} sx={{ mt: 2 }} />
            <Skeleton variant="rectangular" height={120} sx={{ mt: 2 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography>{formatApiError(error)}</Typography>
        </Alert>
        <Button onClick={handleBackToSearch} variant="outlined">
          {t("products.backToSearch")}
        </Button>
      </Container>
    );
  }

  if (!productData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("products.noProductData")}
        </Alert>
        <Button onClick={handleBackToSearch} variant="outlined">
          {t("products.backToSearch")}
        </Button>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        pt: { xs: 2, md: 4 },
        pb: { xs: 3, md: 4 },
        backgroundColor: "#fff",
      }}
    >
      {/* Breadcrumbs */}
      <ProductBreadcrumbs categories={product.categories} />

      {/* Main Product Layout */}
      <Grid container spacing={{ xs: 2, md: 4 }}>
        {/* Left Column - Product Images */}
        <Grid item xs={12} md={4}>
          <Box sx={{ position: "relative" }}>
            <ProductImageGallery
              images={product.images}
              title={product.title}
            />
          </Box>
        </Grid>

        {/* Middle Column - Product Info */}
        <Grid item xs={12} md={5}>
          <ProductInfo
            product={product}
            selectedSize={selectedSize}
            selectedStyle={selectedStyle}
            selectedColor={selectedColor}
            selectedProvider={selectedProvider}
            selectedGrade={selectedGrade}
            selectedCapacity={selectedCapacity}
            selectedSet={selectedSet}
            selectedConfiguration={selectedConfiguration}
            selectedFlavorName={selectedFlavorName}
            showPriceHistory={showPriceHistory}
            isPhoneLocked={isPhoneLocked}
            isAppleCareRestricted={isAppleCareRestricted}
            onSizeChange={setSelectedSize}
            onStyleChange={setSelectedStyle}
            onColorChange={setSelectedColor}
            onProviderChange={setSelectedProvider}
            onGradeChange={setSelectedGrade}
            onCapacityChange={setSelectedCapacity}
            onSetChange={setSelectedSet}
            onConfigurationChange={setSelectedConfiguration}
            onFlavorNameChange={setSelectedFlavorName}
            onPriceHistoryToggle={setShowPriceHistory}
            onVariantSelect={handleVariantSelect}
            showLongDetails={false}
          />
          {!isMobile && hasDetailsTabs && (
            <Box sx={{ mt: 3 }}>
              <ProductDetailsTabs
                activeTab={activeTab}
                onTabChange={handleTabChange}
                product={{
                  title: product.title,
                  categories: product.categories,
                  productDetails: product.productDetails,
                  features: product.features,
                  reviewCount: product.reviewCount,
                  modelNumber: product.modelNumber,
                  upc: product.upc,
                }}
              />
            </Box>
          )}
        </Grid>

        {/* Right Column - Purchase Box */}
        <Grid item xs={12} md={3}>
          <Box
            sx={{
              position: { md: "sticky" },
              top: { md: 96 },
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Product Refresh Status - Above Purchase Box */}
            <ProductRefreshStatus
              isRefreshing={isOffersLoading}
              hasData={!!data}
            />

            <ProductPurchaseBox
              product={productData}
              quantity={quantity}
              onQuantityChange={setQuantity}
              onBuyNow={handleBuyNow}
              shippingData={shippingData || null}
              isPhoneLocked={isPhoneLocked}
              isAppleCareSelected={isAppleCareRestricted}
              isRefreshing={isOffersLoading}
              activeOffer={activeOffer}
              buyBoxOffer={buyBoxOffer}
              allOffers={allOffers && allOffers.length > 0 ? allOffers : productData?.offers}
              onSelectOffer={setSelectedOffer}
              variant={isMobile ? "compact" : "default"}
              showActions={!isMobile}
            />
            {isMobile && (
              <ProductPurchaseActions
                product={productData}
                quantity={quantity}
                onBuyNow={handleBuyNow}
                shippingData={shippingData || null}
                isPhoneLocked={isPhoneLocked}
                isAppleCareSelected={isAppleCareRestricted}
                isRefreshing={isOffersLoading}
                activeOffer={activeOffer}
                layout="column"
              />
            )}

            {/* Shipping Cost Calculation */}
            <Box sx={{ mt: 2 }}>
              <ShippingInfoCard
                shippingCost={shippingData?.shippingCost}
                isCalculating={isShippingLoading}
                error={shippingError}
                onRetry={refetchShipping}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      {isMobile && hasDetailsTabs && (
        <Box sx={{ mt: 3 }}>
          <ProductDetailsTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            product={{
              title: product.title,
              categories: product.categories,
              productDetails: product.productDetails,
              features: product.features,
              reviewCount: product.reviewCount,
              modelNumber: product.modelNumber,
              upc: product.upc,
            }}
          />
        </Box>
      )}

      {/* Important Information */}
      {productData?.important_information && (
        <Box sx={{ mt: 4 }}>
          <ProductImportantInformation data={productData.important_information} />
        </Box>
      )}

      {/* Product Reviews */}
      <Box sx={{ mt: 4 }}>
        <ProductReviews reviews={productData?.reviews} />
      </Box>
    </Container>
  );
};

export default ProductDetailsPage;
